import React, { memo, useEffect, useState } from "react";
import { getOrGenerateBackdropSnapshot, peekBackdropSnapshot, peekBackdropSnapshotStatus } from "@/lib/backdropSnapshot";

/**
 * LetterboxBackdrop — fills the empty space around an `object-contain` scene
 * so the viewer never shows hard black bars.
 *
 * Source preference (cheapest → most expensive):
 *   1. `scene.poster_url`              — static image
 *   2. static-image scene `media_url`
 *   3. image-shaped `media_url`        — only if not a video
 *   4. one-time per-session snapshot   — captured from the visible video
 *      once loaded, blurred via CSS. No extra decoder or media request.
 *   5. `project.accent_color` fill
 *   6. solid black
 *
 * Important UX rules (per V1 spec):
 *   - Must not affect zone mapping / foreground / scene loading
 *   - Must never show errors to public users
 *   - Must gracefully degrade on CORS-tainted media / Safari limitations
 *   - Snapshot is auto-disabled on low-memory devices
 *
 * Debug signals on the root: `data-backdrop-source` (poster|image|snapshot|
 * brand_color|black) and `data-snapshot-status` (idle|pending|ok|failed).
 */
function LetterboxBackdrop({ scene, project }) {
  const treatment = project?.letterbox_treatment || "blurred_backdrop";
  const brand = project?.accent_color || "#0c0c10";

  // ---- Plan the source up-front so every hook runs every render ----------
  const isVideoScene = scene?.type === "loop_video" || scene?.type === "scrubbable" || scene?.type === "transition";
  const looksLikeImageUrl = (u) => typeof u === "string" && /\.(jpe?g|png|webp|gif|avif|svg)(\?|$)/i.test(u);
  let staticSrc = null;
  let sourceKind = null;
  if (treatment === "blurred_backdrop") {
    if (scene?.poster_url) { staticSrc = scene.poster_url; sourceKind = "poster"; }
    else if (scene?.type === "static_image" && scene?.media_url) { staticSrc = scene.media_url; sourceKind = "image"; }
    else if (!isVideoScene && looksLikeImageUrl(scene?.media_url)) { staticSrc = scene.media_url; sourceKind = "image"; }
  }
  const videoUrlForSnapshot =
    treatment === "blurred_backdrop" && !staticSrc && isVideoScene && scene?.media_url ? scene.media_url : null;

  // ---- Hooks: called unconditionally on every render --------------------
  const [snapshotUrl, setSnapshotUrl] = useState(() =>
    videoUrlForSnapshot ? peekBackdropSnapshot(videoUrlForSnapshot) : null
  );
  const [snapshotStatus, setSnapshotStatus] = useState(() =>
    videoUrlForSnapshot ? peekBackdropSnapshotStatus(videoUrlForSnapshot) : "idle"
  );
  useEffect(() => {
    if (!videoUrlForSnapshot) {
      setSnapshotUrl(null);
      setSnapshotStatus("idle");
      return;
    }
    let cancelled = false;
    const cached = peekBackdropSnapshot(videoUrlForSnapshot);
    if (cached) { setSnapshotUrl(cached); setSnapshotStatus("ok"); return; }
    setSnapshotUrl(null);
    setSnapshotStatus("pending");
    let started = false;
    const absoluteUrl = new URL(videoUrlForSnapshot, window.location.href).href;
    const capture = (video) => {
      if (started || !video?.matches?.('video[data-testid="scene-media-video"]')
        || (video.currentSrc || video.src) !== absoluteUrl || video.readyState < 2
        || !video.videoWidth || !video.videoHeight) return;
      started = true;
      getOrGenerateBackdropSnapshot(videoUrlForSnapshot, video).then((url) => {
        if (cancelled) return;
        setSnapshotUrl(url || null);
        setSnapshotStatus(url ? "ok" : "failed");
      });
    };
    const onMediaReady = event => capture(event.target);
    // Media events do not bubble, so listen in capture phase. The immediate
    // scan also covers a video that loaded before this effect was installed.
    document.addEventListener("loadeddata", onMediaReady, true);
    document.addEventListener("playing", onMediaReady, true);
    document.querySelectorAll('video[data-testid="scene-media-video"]').forEach(capture);
    return () => {
      cancelled = true;
      document.removeEventListener("loadeddata", onMediaReady, true);
      document.removeEventListener("playing", onMediaReady, true);
    };
  }, [videoUrlForSnapshot]);

  // ---- Render branches (no hooks below this line) -----------------------
  if (treatment === "black") {
    return (
      <div
        className="absolute inset-0 bg-black"
        aria-hidden="true"
        data-testid="viewer-letterbox-black"
        data-backdrop-source="black"
      />
    );
  }
  if (treatment === "brand_color") {
    return (
      <div
        className="absolute inset-0"
        aria-hidden="true"
        style={{ background: brand }}
        data-testid="viewer-letterbox-brand"
        data-backdrop-source="brand_color"
      />
    );
  }

  // blurred_backdrop: choose final source (static > snapshot > brand fill)
  let renderSrc = staticSrc;
  let renderSource = sourceKind;
  if (!renderSrc && snapshotUrl) { renderSrc = snapshotUrl; renderSource = "snapshot"; }

  if (!renderSrc) {
    return (
      <div
        className="absolute inset-0"
        aria-hidden="true"
        style={{ background: brand }}
        data-testid="viewer-letterbox-fallback"
        data-backdrop-source="brand_color"
        data-snapshot-status={snapshotStatus}
      />
    );
  }

  return (
    <div
      className="absolute inset-0 overflow-hidden"
      aria-hidden="true"
      data-testid="viewer-letterbox-blur"
      data-backdrop-source={renderSource}
      data-snapshot-status={snapshotStatus}
    >
      <img
        src={renderSrc}
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
        style={{ filter: "blur(36px) saturate(1.05)", transform: "scale(1.18)" }}
        draggable={false}
      />
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.32)" }} />
    </div>
  );
}

export default memo(LetterboxBackdrop);
